export const APP_MANIFEST_FEATURE = 'app-manifest'

export type AppManifestValues = {
  title?: string
  themeColor?: string
  appName?: string
  appleAppName?: string
  manifestHref?: string
  icon16?: string
  icon32?: string
  appleTouchIcon?: string
  shortcutIcon?: string
  icons?: Partial<AppManifestValues>
}

type AppManifestProperty = {
  selector?: string
  attr?: string
  get?: () => string | null
  set?: (value: string) => void
}

type AppManifestEntry = {
  id: number
  scope: string
  previousValues: Record<string, string | null>
  appliedValues: Record<string, string>
}

const WILL_NAVIGATE_EVENT = 'ae-will-navigate'

const PROPERTIES: Record<string, AppManifestProperty> = {
  title: {
    get: () => getDocument()?.title ?? null,
    set: (value) => {
      const doc = getDocument()
      if (doc) doc.title = value
    },
  },
  themeColor: {
    selector: 'meta[name="theme-color"]',
    attr: 'content',
  },
  appName: {
    selector: 'meta[name="application-name"]',
    attr: 'content',
  },
  appleAppName: {
    selector: 'meta[name="apple-mobile-web-app-title"]',
    attr: 'content',
  },
  manifestHref: {
    selector: 'link[rel="manifest"]',
    attr: 'href',
  },
  icon16: {
    selector: 'link[rel="icon"][sizes="16x16"]',
    attr: 'href',
  },
  icon32: {
    selector: 'link[rel="icon"][sizes="32x32"]',
    attr: 'href',
  },
  appleTouchIcon: {
    selector: 'link[rel="apple-touch-icon"]',
    attr: 'href',
  },
  shortcutIcon: {
    selector: 'link[rel="shortcut icon"]',
    attr: 'href',
  },
}

function getDocument(): Document | null {
  return typeof document !== 'undefined' ? document : null
}

function getWindow(): Window | null {
  return typeof window !== 'undefined' ? window : null
}

function getValue(key: string) {
  const prop = PROPERTIES[key]
  if (!prop) return undefined

  if (prop.get) return prop.get()

  const doc = getDocument()
  const el = doc?.querySelector(prop.selector)
  return el ? el.getAttribute(prop.attr) : null
}

function setValue(key: string, value: string) {
  const prop = PROPERTIES[key]
  if (!prop) return

  if (prop.set) {
    prop.set(value)
    return
  }

  const doc = getDocument()
  const el = doc?.querySelector(prop.selector)
  if (el) {
    el.setAttribute(prop.attr, value)
  }
}

function normalizeInput(values: Partial<AppManifestValues> = {}) {
  const result: Record<string, string> = {}

  for (const [key, value] of Object.entries(values)) {
    if (key === 'icons' && value && typeof value === 'object') {
      Object.assign(result, normalizeInput(value))
    } else if (typeof value === 'string') {
      result[key] = value
    }
  }

  if (result.appName && !result.appleAppName) {
    result.appleAppName = result.appName
  }

  return result
}

export class AppManifest {
  private _stack: AppManifestEntry[] = []
  private _nextEntryId = 0
  private _baseline: Record<string, string | null> = {}
  private _boundOnWillNavigate: (() => void) | null = null

  constructor() {
    if (!getDocument()) return

    this._captureBaseline()
    this._boundOnWillNavigate = () => this._onWillNavigate()
    getWindow()?.addEventListener(WILL_NAVIGATE_EVENT, this._boundOnWillNavigate)
  }

  get title() { return getValue('title') }
  get themeColor() { return getValue('themeColor') }
  get appName() { return getValue('appName') }
  get manifestHref() { return getValue('manifestHref') }

  get icons() {
    return {
      icon16: getValue('icon16'),
      icon32: getValue('icon32'),
      appleTouchIcon: getValue('appleTouchIcon'),
      shortcutIcon: getValue('shortcutIcon'),
    }
  }

  configureDefaults(values: Partial<AppManifestValues> = {}) {
    const normalized = normalizeInput(values)
    if (Object.keys(normalized).length === 0) return

    this._baseline = { ...this._baseline, ...normalized }
    this._recomputeDOM()
  }

  set(values: Partial<AppManifestValues>, { scope = 'global' } = {}) {
    const normalized = normalizeInput(values)
    const keys = Object.keys(normalized)
    const previousValues = this._captureValues(keys)
    const id = ++this._nextEntryId

    this._stack.push({ id, scope, previousValues, appliedValues: normalized })
    this._applyValues(normalized)

    let restored = false
    return {
      id,
      restore: () => {
        if (restored) return
        restored = true
        this._removeEntry(id)
        this._recomputeDOM()
      }
    }
  }

  async withOverride<T>(values: Partial<AppManifestValues>, callback: () => Promise<T> | T) {
    const handle = this.set(values, { scope: 'temporary' })
    try {
      return await callback()
    } finally {
      handle.restore()
    }
  }

  reset() {
    this._stack = []
    this._recomputeDOM()
  }

  destroy() {
    this.reset()
    if (this._boundOnWillNavigate) {
      getWindow()?.removeEventListener(WILL_NAVIGATE_EVENT, this._boundOnWillNavigate)
      this._boundOnWillNavigate = null
    }
  }

  private _captureBaseline() {
    this._baseline = {}
    for (const key of Object.keys(PROPERTIES)) {
      this._baseline[key] = getValue(key) ?? null
    }
  }

  private _captureValues(keys: string[]) {
    const values: Record<string, string | null> = {}
    for (const key of keys) {
      values[key] = getValue(key) ?? null
    }
    return values
  }

  private _applyValues(values: Record<string, string | null>) {
    for (const [key, value] of Object.entries(values)) {
      if (value != null) {
        setValue(key, value)
      }
    }
  }

  private _removeEntry(id: number) {
    this._stack = this._stack.filter(e => e.id !== id)
  }

  private _recomputeDOM() {
    this._applyValues(this._baseline)
    for (const entry of this._stack) {
      this._applyValues(entry.appliedValues)
    }
  }

  private _onWillNavigate() {
    const navEntries = this._stack.filter(e => e.scope === 'navigation')
    if (navEntries.length === 0) return

    for (const entry of navEntries.reverse()) {
      this._removeEntry(entry.id)
    }
    this._recomputeDOM()
  }
}
